import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import uniq from "lodash/uniq";

import { usePreviewModeContext } from "@contexts/preview-mode-context";
import { useSendItemsContext } from "@contexts/send-items-context";
import { useStoreContext } from "@components/store/provider";

import { showSuccessToast } from "@stadium/services/react-toastify";

import { getStorePreviewModeCartKey } from "@helpers/store";
import { useWindowStorage } from "@hooks/use-window-storage";

import { getCartOrder, getCartOrderId } from "@selectors/cart";
import { getStoreBalance, getStoreCurrencyCode, getStoreNumber } from "@selectors/store";
import { updateCart } from "@slices/cart";

import { isSwagProduct } from "@helpers/products/types";
import { getZoneListFromProductList } from "@helpers/orders/cart";

import { itemRemovedSuccessMessage, itemUpdatedSuccessMessage } from "@constants/orders/cart";
import {
  PREVIEW_MODE_DEFAULT_CURRENCY_ID,
  PREVIEW_MODE_DEFAULT_ORDER_ID,
} from "@constants/preview-mode";
import { ITEM_REMOVE_SUCCESS_TOAST_ID, ITEM_UPDATE_SUCCESS_TOAST_ID } from "@constants/toast-ids";
import {
  SPREE_MEDLEY_ITEMS_SETS,
  SPREE_ORDER_PRODUCTS_SETS,
  ON_DEMAND_PRODUCT,
} from "@constants/products/detail";

const emptyArray = [];
const DEFAULT_ORDER = {
  id: PREVIEW_MODE_DEFAULT_ORDER_ID,
  number: PREVIEW_MODE_DEFAULT_ORDER_ID,
  billedCurrencyId: PREVIEW_MODE_DEFAULT_CURRENCY_ID,
};

const getImageFromVariant = (variant) => (variant?.image ? [variant.image] : emptyArray);

const findAndUpdateItem = (list, findby = {}, updatedValue = {}, newItem = undefined) => {
  const newList = [...list];
  const existingProductIndex = newList.findIndex((item) =>
    Object.keys(findby).every((key) => findby[key] === item[key]),
  );
  if (existingProductIndex !== -1) {
    newList[existingProductIndex] = {
      ...newList[existingProductIndex],
      ...updatedValue,
    };
  } else if (newItem) {
    newList.push(newItem);
  }
  return newList;
};

const modifyOrderLineItems = (lineItems, product) => {
  const { productId, storeId, isCollectionType } = product;
  const newLineItems = [...lineItems];
  const existingProductIndex = newLineItems.findIndex(
    (item) => item.productId === productId && storeId === item.storeId,
  );

  if (!isCollectionType && existingProductIndex !== -1) {
    newLineItems[existingProductIndex] = product;
  } else {
    newLineItems.push(product);
  }

  return newLineItems;
};

const usePopulateLocalItems = () => {
  const dispatch = useDispatch();
  const storeNumber = useSelector(getStoreNumber);
  const storeCurrencyCode = useSelector(getStoreCurrencyCode);
  const { isGuestOrPreviewMode } = usePreviewModeContext();
  const { isSendToMyself } = useSendItemsContext();

  const orderId = useSelector(getCartOrderId);
  const cartOrder = useSelector(getCartOrder);
  const storeSpecificBalance = useSelector(getStoreBalance);

  const { countryCode } = useStoreContext();

  const [, setLocalCartOrder, resetLocalCart] = useWindowStorage(
    getStorePreviewModeCartKey(storeNumber, countryCode, isSendToMyself),
    isGuestOrPreviewMode ? cartOrder : undefined,
  );

  const getItemCountAndTotal = (order) => {
    const { lineItems = [], medleyItemSets = [] } = order;
    return [...lineItems, ...medleyItemSets].reduce(
      (acc, item) => {
        acc.count += item.quantity;
        acc.total += item.price * item.quantity;
        return acc;
      },
      { count: 0, total: 0 },
    );
  };

  const getCartProductZoneList = (order) => {
    const { lineItems = [], medleyItemSets = [] } = order;
    const lineItemZones = getZoneListFromProductList(lineItems);
    const medleyItemSetZones = getZoneListFromProductList(medleyItemSets);
    const cartProductZoneList = uniq([...lineItemZones, ...medleyItemSetZones]).filter(
      (zone) => zone,
    );
    return cartProductZoneList;
  };

  const updateLocalCart = useCallback(
    (order) => {
      //create new order
      if (!order.id) {
        order = { ...order, ...DEFAULT_ORDER, isLocalCartOrder: true };
        order.lineItems = order.lineItems ?? emptyArray;
        order.medleyItemSets = order.medleyItemSets ?? emptyArray;
        order.sendToMany = !isSendToMyself;
        order.useStoreSpecificPoint = isGuestOrPreviewMode;
      }
      const { count, total } = getItemCountAndTotal(order);
      order.itemCount = count;
      order.bagTotal = total;
      order.cartProductZoneList = getCartProductZoneList(order);
      order.currency = order.currency || storeCurrencyCode;
      order.cartTotals = {
        outstandingBalance: order.useStoreSpecificPoint
          ? Math.max(total - storeSpecificBalance, 0)
          : total,
        shopPointsContribution: order.useStoreSpecificPoint
          ? Math.min(storeSpecificBalance, total)
          : 0,
      };
      order.checkoutTotals = order.cartTotals;
      return Promise.resolve(setLocalCartOrder(order)).then(() => {
        dispatch(updateCart(order));
        return order;
      });
    },
    [orderId, storeCurrencyCode, isGuestOrPreviewMode],
  );

  const addItemInOrder = (
    {
      id,
      productId,
      quantity = 1,
      storePrice,
      variant,
      restrictions,
      dietaryRestrictions = {},
      dietaryPreference = "",
      storeId,
      vendorId,
      isCollectionType = false,
      zoneId = "",
      intent = "",
      product: productData = {},
      askForRestriction = false,
      askForPreference = false,
      itemType = ON_DEMAND_PRODUCT,
      isRestrictionsSelected = false,
    },
    existingOrder,
  ) => {
    const product = {
      ...variant,
      id,
      price: storePrice,
      quantity,
      productId,
      images: getImageFromVariant(variant),
      restrictions: restrictions ? restrictions.split(",") : emptyArray,
      isRestrictionsSelected,
      dietaryRestriction: Object.keys(dietaryRestrictions),
      restrictionsForPayload: restrictions,
      dietaryRestrictionForPayload: dietaryRestrictions,
      dietaryPreference,
      localCartCurrency: storeCurrencyCode,
      optionValues: variant.previewModeOptionvalue || emptyArray,
      storeId,
      vendorId,
      isCollectionType,
      zoneId,
      intent,
      product: productData,
      itemType,
      ...(isCollectionType && {
        type: SPREE_ORDER_PRODUCTS_SETS,
        askForRestriction,
        askForPreference,
      }),
    };
    const order = existingOrder || { lineItems: emptyArray, ...cartOrder };
    order.lineItems = modifyOrderLineItems(order.lineItems, product);
    return order;
  };

  const addItemInLocalCart = (payload) => {
    return updateLocalCart(addItemInOrder(payload));
  };

  const addBulkItemsInLocalCart = (products) => {
    const updatedProducts = products.map(
      ({
        variant,
        storePrice,
        quantity = 1,
        productId,
        restrictions,
        dietaryRestrictions = {},
        dietaryPreference = "",
        storeId,
        vendorId,
        zoneId = "",
        isMaster = false,
        itemType = ON_DEMAND_PRODUCT,
        shippedSeparately = false,
      }) => ({
        ...variant,
        id: productId,
        price: storePrice,
        quantity,
        productId,
        images: getImageFromVariant(variant),
        restrictions: restrictions ? restrictions.split(",") : emptyArray,
        dietaryRestriction: Object.keys(dietaryRestrictions),
        dietaryPreference,
        localCartCurrency: storeCurrencyCode,
        optionValues: variant?.previewModeOptionvalue || emptyArray,
        storeId,
        vendorId,
        isBulkAvailable: true,
        zoneId,
        intent: variant?.product?.intent || "",
        isMaster,
        itemType,
        shippedSeparately,
      }),
    );
    const order = { lineItems: emptyArray, ...cartOrder };
    let lineItems = [...order.lineItems];

    updatedProducts.forEach((product) => {
      lineItems = findAndUpdateItem(
        lineItems,
        { productId: product.productId, storeId: product.storeId },
        { quantity: product.quantity },
        product,
      );
    });
    order.lineItems = lineItems.filter(({ quantity }) => quantity);
    return updateLocalCart(order);
  };

  const addByoItemInLocalCart = ({
    productId,
    quantity = 1,
    storePrice,
    variant,
    medleyItemAttributes,
    medleyId,
    zoneId = "",
    intent = "",
    itemType = ON_DEMAND_PRODUCT,
  }) => {
    const product = {
      ...variant,
      id: Date.now().toString(),
      price: storePrice,
      quantity,
      productId,
      images: getImageFromVariant(variant),
      localCartCurrency: storeCurrencyCode,
      medleyComponents: medleyItemAttributes.map((item) => ({
        ...item,
        localCartCurrency: storeCurrencyCode,
        price: item.storePrice,
        zoneId,
      })),
      medleyId,
      zoneId,
      type: SPREE_MEDLEY_ITEMS_SETS,
      intent,
      itemType,
    };
    const order = { medleyItemSets: emptyArray, ...cartOrder };
    const medleyItemSets = [...order.medleyItemSets];
    medleyItemSets.push(product);
    order.medleyItemSets = medleyItemSets;
    return updateLocalCart(order);
  };

  const updateByoComponentsInLocalCart = ({ price, medleyItemSetId, medleyItemAttributes }) => {
    const order = { ...cartOrder };
    order.medleyItemSets = findAndUpdateItem(
      order.medleyItemSets,
      { id: medleyItemSetId },
      {
        medleyComponents: medleyItemAttributes,
        price,
      },
    );
    return updateLocalCart(order);
  };

  const deleteItemFromOrder = ({ productId, storeId, medleyItemSetId }) => {
    // for cases we get productId in array
    productId = [productId].flat();
    const order = { ...cartOrder };
    order.medleyItemSets = [...order.medleyItemSets].filter((item) => item.id !== medleyItemSetId);
    order.lineItems = [...order.lineItems].filter(
      (item) => !(item.storeId === storeId && productId.includes(item.productId)),
    );
    return order;
  };

  const deleteItemFromLocalCart = ({ productId, storeId, medleyItemSetId }) => {
    return updateLocalCart(deleteItemFromOrder({ productId, storeId, medleyItemSetId })).then(() =>
      showSuccessToast({
        message: itemRemovedSuccessMessage,
        config: { toastId: ITEM_REMOVE_SUCCESS_TOAST_ID },
      }),
    );
  };

  const deleteSelectedCountryItemsInLocalCart = (zoneId) => {
    const { lineItems = [], medleyItemSets = [] } = cartOrder;
    const newLineItems = lineItems.filter((item) => item.zoneId !== zoneId);
    const newMedleyItemSets = medleyItemSets.filter((item) => item.zoneId !== zoneId);
    return updateLocalCart({
      ...cartOrder,
      lineItems: newLineItems,
      medleyItemSets: newMedleyItemSets,
    });
  };

  const updateItemInLocalCart = ({ productId, medleyItemSetId, storeId, quantity }) => {
    const order = { ...cartOrder };
    order.medleyItemSets = findAndUpdateItem(
      order.medleyItemSets,
      { id: medleyItemSetId, storeId },
      {
        quantity,
      },
    );
    order.lineItems = findAndUpdateItem(order.lineItems, { productId, storeId }, { quantity });
    return updateLocalCart(order).then(() =>
      showSuccessToast({
        message: itemUpdatedSuccessMessage,
        config: { toastId: ITEM_UPDATE_SUCCESS_TOAST_ID },
      }),
    );
  };

  const updateLocalCartCurrency = (currencyId) => {
    const order = { ...cartOrder };
    order.billedCurrencyId = currencyId;
    return updateLocalCart(order);
  };

  const updateLocalCartUseStoreSpecificPoints = (useStoreSpecificPoints) =>
    updateLocalCart({ ...cartOrder, useStoreSpecificPoint: useStoreSpecificPoints });

  const updateSelectedCountryItemsInLocalCart = (selectedCountry) => {
    const { zoneId } = selectedCountry;
    const { lineItems = [], medleyItemSets = [] } = cartOrder;
    const newLineItems = lineItems.filter((item) => item.zoneId === zoneId);
    const newMedleyItemSets = medleyItemSets.filter((item) => item.zoneId === zoneId);
    return updateLocalCart({
      ...cartOrder,
      sendToMany: false,
      lineItems: newLineItems,
      medleyItemSets: newMedleyItemSets,
    });
  };

  const getUpdatedCartData = (lineItems) =>
    lineItems.reduce((acc, item) => {
      if (item.isCollectionType) {
        acc.push({
          ...item,
          dietaryPreference: "",
          dietaryRestriction: [],
          dietaryRestrictionForPayload: {},
          restrictions: [],
          restrictionsForPayload: "",
          isRestrictionsSelected: false,
        });
      } else if (isSwagProduct(item.intent) && !item?.isMaster) {
        acc.push({
          ...item,
          ...item.product.masterVariant,
          id: item.product.masterVariant.externalId,
          productId: item.product.masterVariant.externalId,
        });
      } else {
        acc.push(item);
      }
      return acc;
    }, []);

  const updateSendToManyToggleItemsInLocalCart = (isSendToOther) => {
    const { lineItems = [] } = cartOrder;
    let newLineItems = [];
    if (isSendToOther) {
      newLineItems = getUpdatedCartData(lineItems);
    } else {
      newLineItems = lineItems;
    }
    const updateLocalCartOrder = {
      ...cartOrder,
      sendToMany: isSendToOther,
      lineItems: newLineItems,
    };
    return Promise.resolve(dispatch(updateCart(updateLocalCartOrder)));
  };

  const deleteAndAddItemInCart = ({ oldProductId, oldStoreId, newPayload }) => {
    const updatedOrder = addItemInOrder(
      newPayload,
      deleteItemFromOrder({ productId: oldProductId, storeId: oldStoreId }),
    );
    return updateLocalCart(updatedOrder);
  };
  return {
    addItemInLocalCart,
    addBulkItemsInLocalCart,
    deleteItemFromLocalCart,
    updateItemInLocalCart,
    resetLocalCart,
    updateLocalCartCurrency,
    updateSelectedCountryItemsInLocalCart,
    updateSendToManyToggleItemsInLocalCart,
    addByoItemInLocalCart,
    updateByoComponentsInLocalCart,
    deleteAndAddItemInCart,
    deleteSelectedCountryItemsInLocalCart,
    updateLocalCartUseStoreSpecificPoints,
  };
};

export default usePopulateLocalItems;
